import React, { useEffect, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, Divider, Typography } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { observer } from "mobx-react-lite";
import { makeStyles, withStyles } from "tss-react/mui";

import { RuleKind } from "@/gql/graphql";

import IntervalItem from "./IntervalItem";
import { useSchedule } from "../SchedulerProvider";
import Interval from "../store/Interval";
import Rule from "../store/Rule";

type Props = {
  visible: boolean;
  onClose: () => void;
};

const StyledButton = withStyles(Button, {
  root: {
    height: 32,
    minWidth: 124,
  },
});

const StyledDialog = withStyles(Dialog, {
  paper: {
    maxWidth: 800,
  },
});

const useStyles = makeStyles()(() => ({
  root: {
    width: "100%",
    marginTop: 8,
    marginBottom: 14,
  },
  icon: {
    color: "#7D8797",
    height: 20,
    width: 20,
  },
}));

const OverrideDateDialog: React.FC<Props> = observer(({ visible, onClose }) => {
  const { classes } = useStyles();
  const schedule = useSchedule();
  const [newRule, setNewRule] = useState(new Rule(schedule, RuleKind.Date));

  const displayInterval = newRule.date != null;

  useEffect(() => {
    // Whenever this dialog is shown/hidden, we'll create a new rule, so that
    // any change inside this dialog won't be saved immediately to schedule
    // until "Apply" is clicked.
    setNewRule(new Rule(schedule, RuleKind.Date));
  }, [visible]);

  function handleAdd() {
    schedule.saveOverrideRule(newRule);
    onClose();
  }

  function handleClose() {
    onClose();
  }

  function renderIntervals() {
    if (newRule.presentIntervals.length > 0) {
      return newRule.presentIntervals.map(
        (e: Interval, intervalIndex: number) => (
          <IntervalItem key={intervalIndex} interval={e} rule={newRule} />
        )
      );
    } else {
      return <Typography variant="h6">Unavailable</Typography>;
    }
  }

  return (
    <StyledDialog
      open={visible}
      onClose={() => {
        onClose();
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        margin={4}
      >
        <DialogTitle fontWeight="bold">Select dates to override</DialogTitle>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box display="flex" paddingRight={4} paddingLeft={4} paddingBottom={4}>
        <div>
          <StaticDatePicker
            onAccept={(date: Date | null) => {
              newRule.setDate(date);
              // If there's an existing rule for this date, copy its intervals
              // to the new rule to make it look like we're editing an existing
              // rule's intervals.
              const existing = schedule.findOverrideRule(date);
              if (existing) schedule.copyIntervals(existing, [newRule]);
            }}
            disablePast
            displayStaticWrapperAs="desktop"
          />
          <Divider light />
          <Box
            marginTop={4}
            display="flex"
            justifyContent="space-between"
            paddingLeft={4}
            paddingRight={4}
          >
            <StyledButton variant="outlined" onClick={handleClose}>
              Cancel
            </StyledButton>
            <StyledButton
              disabled={!schedule.isFormValid}
              type="submit"
              onClick={handleAdd}
            >
              Apply
            </StyledButton>
          </Box>
        </div>
        {displayInterval && (
          <Box marginLeft={8} display="column">
            <Box display="flex" justifyContent="space-between" marginBottom={8}>
              <Typography variant="h6" fontWeight="bold">
                What hours are you available?
              </Typography>
              <IconButton
                sx={{ marginLeft: 8 }}
                onClick={() => {
                  newRule.addInterval();
                }}
              >
                <AddIcon className={classes.icon} />
              </IconButton>
            </Box>
            {renderIntervals()}
          </Box>
        )}
      </Box>
    </StyledDialog>
  );
});

export default OverrideDateDialog;
